import { useRef, useState } from 'react'
import { motion, useInView, AnimatePresence } from 'framer-motion'
import { Crosshair, ChevronRight, X } from 'lucide-react'
import { arsenal } from '../../data/siteData'

export default function Arsenal() {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })
  const [selectedWeapon, setSelectedWeapon] = useState<typeof arsenal[number] | null>(null)

  return (
    <section id="arsenal" className="relative py-24 sm:py-32 bg-section-dark grain-overlay" style={{ backgroundColor: 'rgba(10,14,10,0.55)', backdropFilter: 'blur(2px)' }}>
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-military/40 to-transparent" />

      <div ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <span className="font-mono text-xs text-military-light tracking-[0.4em] uppercase">
            Sprzęt
          </span>
          <h2 className="font-display text-5xl sm:text-6xl md:text-7xl text-bone mt-4 tracking-wider">
            NASZ ARSENAŁ
          </h2>
          <p className="font-body text-chrome mt-4 max-w-2xl mx-auto">
            Repliki, które czujesz w dłoniach.
          </p>
          <div className="w-24 h-0.5 bg-military-light mx-auto mt-6" />
        </motion.div>

        {/* Arsenal Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {arsenal.map((weapon, i) => (
            <motion.div
              key={weapon.id}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="group relative bg-[#1A1A1A]/70 border border-military/20 rounded-sm overflow-hidden hover:border-military/50 hover:-translate-y-1 transition-all duration-300 cursor-pointer"
              onClick={() => setSelectedWeapon(weapon)}
            >
              {/* Image */}
              <div className="relative aspect-[4/3] overflow-hidden bg-white">
                <img
                  src={weapon.image}
                  alt={weapon.name}
                  className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-steel-light/80 via-transparent to-transparent pointer-events-none" />

                {/* Type Badge */}
                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1 bg-military/80 text-bone font-mono text-[10px] uppercase tracking-wider rounded-sm backdrop-blur-sm">
                    {weapon.type}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="font-subheading font-bold text-xl text-bone mb-2 group-hover:text-military-light transition-colors">
                  {weapon.name}
                </h3>
                <p className="font-body text-sm text-chrome-light leading-relaxed mb-4">
                  {weapon.description}
                </p>

                {/* Specs */}
                <div className="flex flex-wrap gap-2">
                  <span className="px-2 py-1 bg-steel-lighter border border-military/20 rounded-sm font-mono text-[10px] text-chrome">
                    {weapon.specs.range}
                  </span>
                  <span className="px-2 py-1 bg-steel-lighter border border-military/20 rounded-sm font-mono text-[10px] text-chrome">
                    {weapon.specs.ammo}
                  </span>
                  <span className="px-2 py-1 bg-steel-lighter border border-military/20 rounded-sm font-mono text-[10px] text-chrome">
                    FPS {weapon.specs.fps}
                  </span>
                </div>
              </div>

              {/* Hover Line */}
              <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-military-light group-hover:w-full transition-all duration-500" />
            </motion.div>
          ))}
        </div>

        {/* Bottom Text */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.5, delay: 1 }}
          className="mt-12 text-center"
        >
          <p className="font-body text-sm text-chrome max-w-2xl mx-auto">
            Wszystkie repliki są regularnie serwisowane i gotowe do akcji.
            Dbamy o to, żebyś mógł skupić się tylko na misji.
          </p>
        </motion.div>
      </div>

      {/* Weapon Detail Modal */}
      <AnimatePresence>
        {selectedWeapon && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
            onClick={() => setSelectedWeapon(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: 'spring', damping: 25 }}
              className="bg-[#1A1A1A]/95 border border-military/30 rounded-sm max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="relative bg-gradient-to-br from-steel-lighter to-steel">
                <img
                  src={selectedWeapon.image}
                  alt={selectedWeapon.name}
                  className="w-full aspect-video object-contain p-6"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-steel-light/80 via-transparent to-transparent pointer-events-none" />
                <button
                  onClick={() => setSelectedWeapon(null)}
                  className="absolute top-4 right-4 p-2 bg-steel/80 backdrop-blur-sm rounded-sm text-chrome hover:text-bone transition-colors"
                >
                  <X size={20} />
                </button>
                <div className="absolute bottom-4 left-6">
                  <span className="px-3 py-1 bg-military/80 text-bone font-mono text-[10px] uppercase tracking-wider rounded-sm backdrop-blur-sm">
                    {selectedWeapon.type}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 sm:p-8">
                <h3 className="font-display text-3xl text-bone tracking-wider mb-2">
                  {selectedWeapon.name}
                </h3>
                <p className="font-body text-chrome-light leading-relaxed mb-6">
                  {selectedWeapon.description}
                </p>

                {/* Specs */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="p-4 bg-steel-lighter border border-military/20 rounded-sm text-center">
                    <Crosshair size={20} className="text-military-light mx-auto mb-2" />
                    <p className="font-mono text-xs text-chrome">Zasięg</p>
                    <p className="font-subheading font-bold text-bone">{selectedWeapon.specs.range}</p>
                  </div>
                  <div className="p-4 bg-steel-lighter border border-military/20 rounded-sm text-center">
                    <Crosshair size={20} className="text-military-light mx-auto mb-2" />
                    <p className="font-mono text-xs text-chrome">Amunicja</p>
                    <p className="font-subheading font-bold text-bone">{selectedWeapon.specs.ammo}</p>
                  </div>
                  <div className="p-4 bg-steel-lighter border border-military/20 rounded-sm text-center">
                    <Crosshair size={20} className="text-military-light mx-auto mb-2" />
                    <p className="font-mono text-xs text-chrome">FPS</p>
                    <p className="font-subheading font-bold text-bone">{selectedWeapon.specs.fps}</p>
                  </div>
                </div>

                <button
                  onClick={() => {
                    setSelectedWeapon(null)
                    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })
                  }}
                  className="w-full py-3 bg-military-light text-bone font-subheading font-bold uppercase tracking-wider rounded-sm hover:bg-military-glow transition-colors flex items-center justify-center gap-2"
                >
                  Zarezerwuj tę replikę
                  <ChevronRight size={16} />
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  )
}
